/**
 * useTrackRecsClick — fires CIO `recommendation_click` beacon on tile click
 * from any recs carousel (PDP cross-sell, PDP related products, ATC flyout
 * recs, home/cart recs).
 *
 * Spike `cio-path-a-architecture.md` §2 — every recs surface must emit
 * `recommendation_click` so CIO's learning loop sees click attribution.
 * Impressions alone (the GET /recommendations/v1/pods response) aren't
 * enough; the click event closes the attribution loop.
 *
 * SDK reference: `node_modules/@constructor-io/constructorio-client-javascript`
 * → `lib/types/tracker.d.ts:152 trackRecommendationClick`.
 *
 * Identity flows automatically via the SDK's `cnstrcUserId` (set pre-hydration
 * by SFCC-10644 in root.tsx) and the SDK-managed `ConstructorioID_client_id`
 * cookie. No additional plumbing required on the caller side.
 */
import { useCallback } from "react";

import { cioSearchTerm } from "~/hec/analytics/cio-sdk-tracking";
import { useCioClient } from "~/utils/constructor";
import { reportClientError } from "~/utils/report-client-error";

interface TrackRecsClickArgs {
  podId: string;
  itemId: string;
  itemName: string;
  /** From CIO response's per-result `strategy.id` (e.g. "bestsellers"). */
  strategyId?: string;
  /** From the top-level `result_id` field on the CIO response. */
  resultId?: string;
  /** 1-indexed position of the clicked tile within the carousel. */
  resultPositionOnPage?: number;
  numResultsPerPage?: number;
  /** CIO index section of the clicked item; the SDK defaults to "Products". */
  section?: string;
}

export function useTrackRecsClick() {
  const cio = useCioClient();
  return useCallback(
    (args: TrackRecsClickArgs) => {
      // SFCC-11961: a recommendation click ends search attribution for the
      // add-to-cart conversion.
      cioSearchTerm.clear();
      if (!cio) return;
      try {
        cio.tracker.trackRecommendationClick({
          podId: args.podId,
          // SDK type requires non-empty strings even though semantically optional.
          strategyId: args.strategyId ?? "",
          itemId: args.itemId,
          itemName: args.itemName,
          resultId: args.resultId,
          resultPositionOnPage: args.resultPositionOnPage,
          numResultsPerPage: args.numResultsPerPage,
          section: args.section,
        });
      } catch (err) {
        // Never let beacon failures break the click navigation.
        console.warn("CIO trackRecommendationClick failed:", err);
        reportClientError("cio.trackRecommendationClick", err, {
          podId: args.podId,
        });
      }
    },
    [cio],
  );
}
